import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import AgendaBuilder, { AgendaItem } from '../components/AgendaBuilder';
import InMeetingAssistant from '../components/InMeetingAssistant';
import ActionItemList, { ActionItem } from '../components/ActionItemList';
import { taskboardService, Column, Task, SubTask } from '../services/taskboardService';
import TaskboardSelectionModal from '../components/TaskboardSelectionModal';
import '../styles/MeetingDetailsPage.css';

interface CalendarEvent {
    id: string;
    title: string;
    start: Date;
    end: Date;
    color?: string;
    description?: string;
    provider?: 'google' | 'microsoft';
}

const MeetingDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [event, setEvent] = useState<CalendarEvent | null>(null);
    const [activeTab, setActiveTab] = useState<'pre' | 'in' | 'post'>('pre');
    const [notes, setNotes] = useState({
        pre: '',
        in: '',
        post: ''
    });

    const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
    const [actionItems, setActionItems] = useState<ActionItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);
    const [isTaskboardModalOpen, setIsTaskboardModalOpen] = useState(false);

    useEffect(() => {
        if (id) {
            loadMeetingDetails();
        }
    }, [id]);

    const loadMeetingDetails = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('meeting_plans')
                .select('*')
                .eq('id', id)
                .single();

            if (error) throw error;

            if (data) {
                setEvent({
                    id: data.id,
                    title: data.title,
                    start: new Date(data.start_time),
                    end: new Date(data.end_time),
                    provider: data.provider,
                    description: data.description
                });

                setNotes({
                    pre: data.pre_meeting_notes || '',
                    in: data.in_meeting_notes || '',
                    post: data.post_meeting_notes || ''
                });

                // Try to parse agenda from pre-meeting notes if it looks like JSON
                if (data.pre_meeting_notes && data.pre_meeting_notes.startsWith('[')) {
                    try {
                        const parsed = JSON.parse(data.pre_meeting_notes);
                        if (Array.isArray(parsed)) {
                            setAgendaItems(parsed);
                        }
                    } catch (e) {
                        // Not JSON, keep as text (handled by notes.pre)
                        console.log('Pre-meeting notes are not JSON agenda');
                    }
                }

                // Try to parse action items from post-meeting notes
                if (data.post_meeting_notes) {
                    if (data.post_meeting_notes.startsWith('[')) {
                        try {
                            const parsed = JSON.parse(data.post_meeting_notes);
                            if (Array.isArray(parsed)) {
                                setActionItems(parsed);
                            }
                        } catch (e) {
                            // Not JSON, treat as single item
                            setActionItems([{
                                id: crypto.randomUUID(),
                                text: data.post_meeting_notes,
                                completed: false
                            }]);
                        }
                    } else {
                        // Not JSON, treat as single item
                        setActionItems([{
                            id: crypto.randomUUID(),
                            text: data.post_meeting_notes,
                            completed: false
                        }]);
                    }
                } else {
                    // Empty, initialize with empty list
                    setActionItems([]);
                }
            }
        } catch (error) {
            console.error('Error loading meeting details:', error);
            setToast({ message: 'Failed to load meeting details', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!id) return;

        setSaving(true);
        let contentName = 'Notes';
        if (activeTab === 'pre') contentName = 'Agenda';
        if (activeTab === 'post') contentName = 'Action Items';

        try {
            const { error } = await supabase
                .from('meeting_plans')
                .update({
                    pre_meeting_notes: notes.pre,
                    in_meeting_notes: notes.in,
                    post_meeting_notes: notes.post,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id);

            if (error) throw error;

            setToast({ message: `${contentName} saved successfully`, type: 'success' });
        } catch (error: any) {
            console.error('Error saving notes:', error);
            setToast({
                message: `Failed to save ${contentName.toLowerCase()}: ${error.message || 'Unknown error'}`,
                type: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const handleAddToTaskboard = () => {
        if (actionItems.length === 0) {
            setToast({ message: 'No action items to add', type: 'info' });
            return;
        }
        setIsTaskboardModalOpen(true);
    };

    const handleConfirmAddToTaskboard = async (boardId: string | null) => {
        setIsTaskboardModalOpen(false);
        setSaving(true);

        try {
            // Create subtasks from action items
            const subtasks: SubTask[] = actionItems.map(item => ({
                id: crypto.randomUUID(),
                text: item.text,
                completed: item.completed
            }));

            // Create a single parent task
            const newTask: Task = {
                id: crypto.randomUUID(),
                title: event?.title || 'Meeting Action Items',
                description: `Action items from meeting on ${event?.start.toLocaleDateString()}`,
                status: 'todo',
                priority: 'medium',
                tags: ['Meeting', 'Action Items'],
                createdAt: new Date().toISOString(),
                subtasks: subtasks,
                url: [],
                drive: [],
                figma: [],
                jira: [],
                docs: []
            };

            let targetBoardId = boardId;

            if (!targetBoardId) {
                // Create new board
                const defaultColumns: Column[] = [
                    { id: 'backlog', title: 'Backlog', color: '#64748b', tasks: [] },
                    { id: 'todo', title: 'To Do', color: '#3b82f6', tasks: [newTask] },
                    { id: 'in-progress', title: 'In Progress', color: '#eab308', tasks: [] },
                    { id: 'done', title: 'Done', color: '#22c55e', tasks: [] }
                ];
                const boardName = `Action Items: ${event?.title || 'Meeting'}`;
                const newBoard = await taskboardService.createTaskBoard(boardName, defaultColumns);
                targetBoardId = newBoard.id;
            } else {
                // Add to existing board
                const board = await taskboardService.getTaskBoard(targetBoardId);
                if (board) {
                    // Find 'todo' column or first column
                    const todoColumnIndex = board.columns.findIndex(c => c.id === 'todo' || c.title.toLowerCase().includes('todo')) || 0;
                    if (todoColumnIndex !== -1) {
                        board.columns[todoColumnIndex].tasks.push(newTask);
                    } else if (board.columns.length > 0) {
                        board.columns[0].tasks.push(newTask);
                    }
                    await taskboardService.updateTaskBoard(targetBoardId, { columns: board.columns }, board.updated_at);
                }
            }

            setToast({ message: 'Added to taskboard successfully!', type: 'success' });

            // Navigate to the board
            if (targetBoardId) {
                setTimeout(() => {
                    navigate(`/taskboard/${targetBoardId}`);
                }, 1500);
            }

        } catch (error: any) {
            console.error('Error adding to taskboard:', error);
            setToast({ message: 'Failed to add to taskboard', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="meeting-details-page">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>Loading meeting details...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!event) {
        return (
            <Layout>
                <div className="meeting-details-page">
                    <div className="error-state">
                        <p>Meeting not found</p>
                        <button className="btn-back" onClick={() => navigate('/meeting-plan')}>
                            Back to Calendar
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    const handleAgendaChange = (newItems: AgendaItem[]) => {
        setAgendaItems(newItems);
        // Sync with notes.pre so it persists across tab switches and saves correctly
        setNotes(prev => ({ ...prev, pre: JSON.stringify(newItems) }));
    };

    const handleActionItemsChange = (newItems: ActionItem[]) => {
        setActionItems(newItems);
        // Sync with notes.post
        setNotes(prev => ({ ...prev, post: JSON.stringify(newItems) }));
    };

    const getSaveButtonText = () => {
        if (saving) return 'Saving...';
        switch (activeTab) {
            case 'pre': return 'Save Agenda';
            case 'post': return 'Save Action Items';
            default: return 'Save Notes';
        }
    };

    return (
        <>
            <Layout>
                <div className="meeting-details-page fade-in">
                    <div className="meeting-header">
                        <div className="header-content">
                            <h1>{event.title}</h1>
                            <div className="event-meta">
                                <span className="event-time">
                                    {event.start.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                                </span>
                                {event.provider && (
                                    <span className={`provider-badge ${event.provider}`}>
                                        {event.provider === 'google' ? 'Google Calendar' : 'Microsoft Teams'}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button className="btn-back" onClick={() => navigate('/meeting-plan')}>
                            Back to Calendar
                        </button>
                    </div>

                    <div className="page-tabs">
                        <button
                            className={`tab-btn ${activeTab === 'pre' ? 'active' : ''}`}
                            onClick={() => setActiveTab('pre')}
                        >
                            Pre-Meeting
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'in' ? 'active' : ''}`}
                            onClick={() => setActiveTab('in')}
                        >
                            In-Meeting
                        </button>
                        <button
                            className={`tab-btn ${activeTab === 'post' ? 'active' : ''}`}
                            onClick={() => setActiveTab('post')}
                        >
                            Post-Meeting
                        </button>
                    </div>

                    <div className="page-content">
                        <div className="notes-editor">
                            {activeTab === 'pre' ? (
                                <AgendaBuilder
                                    initialAgenda={agendaItems}
                                    meetingStart={event.start}
                                    meetingDuration={(event.end.getTime() - event.start.getTime()) / 60000}
                                    onChange={handleAgendaChange}
                                />
                            ) : activeTab === 'in' ? (
                                <InMeetingAssistant
                                    initialNotes={notes.in}
                                    onNotesChange={async (newNotes) => {
                                        // Update local state
                                        setNotes({ ...notes, in: newNotes });

                                        // Auto-save to database
                                        if (id) {
                                            try {
                                                await supabase
                                                    .from('meeting_plans')
                                                    .update({
                                                        in_meeting_notes: newNotes,
                                                        updated_at: new Date().toISOString()
                                                    })
                                                    .eq('id', id);
                                            } catch (error) {
                                                console.error('Error auto-saving notes:', error);
                                            }
                                        }
                                    }}
                                    onTranscriptChange={async (transcript) => {
                                        // Update local state
                                        setNotes(prev => ({ ...prev, in: transcript }));

                                        // Auto-save to database
                                        if (id) {
                                            try {
                                                await supabase
                                                    .from('meeting_plans')
                                                    .update({
                                                        in_meeting_notes: transcript,
                                                        updated_at: new Date().toISOString()
                                                    })
                                                    .eq('id', id);
                                            } catch (error) {
                                                console.error('Error auto-saving transcript:', error);
                                            }
                                        }
                                    }}
                                    onMoveActionItems={async (actionItemsText) => {
                                        // Parse action items from text (each line starting with •)
                                        const items = actionItemsText
                                            .split('\n')
                                            .map(line => line.trim())
                                            .filter(line => line.startsWith('•'))
                                            .map(line => line.substring(1).trim())
                                            .filter(text => text.length > 0);

                                        // Add to action items list
                                        const newActionItems: ActionItem[] = items.map((text, index) => ({
                                            id: `moved-${Date.now()}-${index}`,
                                            text,
                                            completed: false,
                                            priority: 'medium' as const
                                        }));

                                        const updatedActionItems = [...actionItems, ...newActionItems];
                                        setActionItems(updatedActionItems);

                                        // Save to database
                                        if (id) {
                                            try {
                                                await supabase
                                                    .from('meeting_plans')
                                                    .update({
                                                        action_items: JSON.stringify(updatedActionItems),
                                                        updated_at: new Date().toISOString()
                                                    })
                                                    .eq('id', id);
                                            } catch (error) {
                                                console.error('Error saving moved action items:', error);
                                            }
                                        }

                                        // Switch to post-meeting tab
                                        setActiveTab('post');
                                    }}
                                />
                            ) : (
                                <ActionItemList
                                    initialItems={actionItems}
                                    onChange={handleActionItemsChange}
                                />
                            )}
                        </div>
                    </div>

                    {activeTab !== 'in' && (
                        <div className="page-footer">
                            {activeTab === 'post' && (
                                <button
                                    className="btn-secondary"
                                    onClick={handleAddToTaskboard}
                                    disabled={saving || actionItems.length === 0}
                                    style={{ marginRight: '12px', padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}
                                >
                                    Add to Taskboard
                                </button>
                            )}
                            <button
                                className="btn-save"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {getSaveButtonText()}
                            </button>
                        </div>
                    )}
                </div>
            </Layout>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <TaskboardSelectionModal
                isOpen={isTaskboardModalOpen}
                onClose={() => setIsTaskboardModalOpen(false)}
                onConfirm={handleConfirmAddToTaskboard}
            />
        </>
    );
};

export default MeetingDetailsPage;
